import React, { useEffect, useState } from 'react';
import styles from './CreateSystemProjectDialog.module.scss';
import { Select, Button, MenuItem, FormControl, Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CreateDialogStateType, CreateDialogType } from '../../pages/Dashboard/CleanDash';
import {
  CreateProjectRequest,
  CreateSystemRequest,
  SystemType,
  TemplateInfo,
} from '../../models/Api';
import SystemService from '../../services/SystemService';
import { useFormik } from 'formik';
import * as yup from 'yup';
import ProjectService from '../../services/ProjectService';

type CreateFormInitialValuesType = {
  systemType: string;
  systemTemplate: string;
  name: string;
  description: string;
};

const CreateSystemProjectDialog = (props: {
  systemTypesAndTemplates: SystemType[];
  dialogState: CreateDialogStateType;
  closeAction: () => void;
}) => {
  const hideLegendStyle = {
    '& legend': { display: 'none' },
    '& fieldset': { top: 0 },
  };

  const { t } = useTranslation();
  const { systemTypesAndTemplates, dialogState, closeAction } = props;
  const [availableTemplates, setAvailableTemplates] = useState([] as TemplateInfo[]);
  const [templateDescription, setTemplateDescription] = useState<string | undefined | null>('');
  const initialFormValues = {
    systemType: '',
    systemTemplate: '',
    name: '',
    description: '',
  };

  const [reinitializedFormValues, setReinitializedFormValues] =
    useState<CreateFormInitialValuesType | null>(null);

  const createProjectValidationSchema = yup.object().shape({
    name: yup.string().required(t('NameFieldIsRequired')),
  });

  const createSysytemValidationSchema = yup.object().shape({
    name: yup.string().required(t('NameFieldIsRequired')),
    systemType: yup.string().required(t('TypeFieldIsRequired')),
    systemTemplate: yup.string().required(t('TemplateFieldIsRequired')),
  });

  const basicValidationSchema =
    dialogState.type === CreateDialogType.Project
      ? createProjectValidationSchema
      : createSysytemValidationSchema;

  const onSubmit = async (values: CreateFormInitialValuesType) => {
    if (dialogState.type === CreateDialogType.Project) {
      const createProjectRequestObject: CreateProjectRequest = {
        name: values.name,
        description: values.description,
      };
      const response = await ProjectService.create(createProjectRequestObject);
      if (response.status === 200) {
        ProjectService.updateRecentlyCreatedProject(response.data);
      }
      closeAction();
      return;
    }

    const templateId = availableTemplates.find(
      (template) => template.name === values.systemTemplate,
    )?.id as number;
    const createSystemRequest: CreateSystemRequest = {
      projectId: dialogState.projectId as string,
      name: values.name,
      templateId: templateId,
      description: values.description,
    };
    const response = await SystemService.createSystem(createSystemRequest);
    if (response.status === 200)
      window.location.href =
        process.env.REACT_APP_LEGACY_BASE_HOST + `/System/System?SystemId=${response.data}`;
    closeAction();
  };

  const { handleChange, handleBlur, handleSubmit, errors, touched, values, resetForm } = useFormik({
    initialValues: reinitializedFormValues || initialFormValues,
    onSubmit,
    validationSchema: basicValidationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    setTemplateDescription('');
    setReinitializedFormValues(initialFormValues);
    resetForm();
  }, [dialogState.show]);

  useEffect(() => {
    const templates = systemTypesAndTemplates.find(
      (systemType) => systemType.name === values.systemType,
    )?.templates;
    setAvailableTemplates(templates as TemplateInfo[]);
    if (templates && templates.length > 0)
      setReinitializedFormValues({
        systemType: values.systemType,
        systemTemplate: templates[0].name,
        name: values.name,
        description: values.description,
      });
    if (values.systemTemplate !== '') {
      const selectedTemplate = templates?.find(
        (template) => template.name === values.systemTemplate,
      );
      setTemplateDescription(selectedTemplate?.description);
    }
  }, [values.systemType, values.systemTemplate]);

  return (
    <Modal
      open={dialogState.show}
      onClose={() => {
        closeAction();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={styles['create-dialog']}>
        <div className={styles['title-div']}>
          <p>
            {dialogState.type === CreateDialogType.System ? t('CreateSystem') : t('CreateProject')}
          </p>
        </div>

        <div className={styles['input-wrapper']}>
          {dialogState.type === CreateDialogType.System && (
            <>
              <div>
                <label>
                  {t('SystemType')}
                  <span className={styles['asterisk-sign']}>*</span>
                </label>
                <FormControl size="small">
                  <Select
                    displayEmpty
                    color="secondary"
                    name="systemType"
                    onChange={(e) => {
                      if (values.systemType) {
                        setReinitializedFormValues({
                          systemType: e.target.value,
                          systemTemplate: '',
                          name: values.name,
                          description: values.description,
                        });
                        setTemplateDescription('');
                      }
                      handleChange(e);
                    }}
                    value={values.systemType ?? ''}
                    renderValue={() => {
                      if (values.systemType === '') {
                        return <span>{t('SelectSystemType')}</span>;
                      }
                      return <span>{values.systemType}</span>;
                    }}
                    className={styles['dropdown-field']}
                    sx={hideLegendStyle}
                  >
                    {systemTypesAndTemplates?.map((systemType) => {
                      return (
                        <MenuItem key={systemType.name} value={systemType.name}>
                          {systemType.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {errors.systemType && touched.systemType && (
                  <p className={styles['error-text']}>{errors.systemType}</p>
                )}
              </div>
              <div>
                <label>
                  {t('SystemTemplate')}
                  <span className={styles['asterisk-sign']}>*</span>
                </label>
                <FormControl size="small">
                  <Select
                    name="systemTemplate"
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    displayEmpty
                    value={
                      availableTemplates?.some(
                        (template: TemplateInfo) => template.name === values.systemTemplate,
                      )
                        ? values.systemTemplate
                        : ''
                    }
                    renderValue={() => {
                      if (values.systemTemplate === '') {
                        return <span>{t('SelectSystemTemplate')}</span>;
                      }
                      return <span>{values.systemTemplate}</span>;
                    }}
                    className={styles['dropdown-field']}
                    sx={hideLegendStyle}
                  >
                    {availableTemplates?.map((template) => (
                      <MenuItem key={template.id} value={template.name}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.systemTemplate && touched.systemTemplate && (
                  <p className={styles['error-text']}>{errors.systemTemplate}</p>
                )}
              </div>
              <div>
                <label>{t('TemplateDescription')}</label>
                <TextField
                  id="templateDescription"
                  maxRows={3}
                  multiline
                  disabled
                  placeholder={t('ViewTemplateDesc...')}
                  defaultValue={templateDescription ?? ''}
                  className={styles['description-field']}
                />
              </div>
            </>
          )}
          <div>
            <label>
              {t('name')}
              <span className={styles['asterisk-sign']}>*</span>
            </label>
            <TextField
              id="name"
              placeholder={
                dialogState.type === CreateDialogType.System
                  ? t('NameOfTheSystem')
                  : t('NameOfTheProject')
              }
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              inputProps={{ maxLength: 256 }}
              className={styles['name-field']}
            />
            {errors.name && touched.name && <p className={styles['error-text']}>{errors.name}</p>}
          </div>
          <div>
            <label>{t('description')}</label>
            <TextField
              placeholder={
                dialogState.type === CreateDialogType.System
                  ? t('DescribeTheSystem')
                  : t('DescribeTheProject')
              }
              maxRows={4}
              multiline
              id="description"
              inputProps={{ maxLength: 4000 }}
              onChange={handleChange}
              value={values.description}
              className={styles['description-field']}
            />
          </div>
        </div>
        <div className={styles['buttons-div']}>
          <Button onClick={() => handleSubmit()}>{t('Add')}</Button>
          <Button onClick={() => props.closeAction()}>{t('Cancel')}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSystemProjectDialog;
